/**
 * One item's history, and the controls for the item itself.
 *
 * This file used to be a whole screen — a flat catalogue with its own search,
 * sort and totals. That screen printed a category name on every row while the
 * Categories screen said nothing about what was filed under each one: the same
 * question asked from two ends. They are one screen now (`Categories.tsx`),
 * and what remains here is the panel that opens under an item.
 *
 * Nothing in the catalogue was ever "set up". Every item exists because it was
 * typed on a line of a real purchase, which is the whole idea: a list you have
 * to maintain is a list that goes stale, and one that assembles itself from
 * what you bought is true by construction.
 *
 * Re-filing an item moves where its FUTURE purchases go. Past entries keep the
 * category they were recorded with; to move one of those, edit that entry.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Category, ProductHistory } from '@et/contracts';
import { BUCKET_LABELS, formatMoney, parseMoney, type Bucket, type Money } from '@et/core';
import { api, messageOf, type Identity } from './api.js';
import { dayLabel } from './months.js';

/**
 * Things you buy and sources of money are both "products" underneath — a name
 * plus a category — but they are not the same thing to a person, and the panel
 * words itself differently for each. The category's bucket is what separates
 * them.
 */
export type Kind = 'bought' | 'income';

const UNIT_LABELS: Record<string, string> = {
  PCS: '×',
  KG: ' kg',
  G: ' g',
  L: ' L',
  ML: ' ml',
};

export function unitSuffix(unit: string): string {
  return UNIT_LABELS[unit] ?? ` ${unit.toLowerCase()}`;
}

/**
 * One item's history. Loaded on expand rather than up front — the list is the
 * screen, and forty purchase histories nobody opened is forty queries wasted.
 */
export function ItemDetail({
  identity,
  id,
  kind,
  categories,
  canEdit,
  onChanged,
  onDeleted,
}: {
  identity: Identity;
  id: string;
  kind: Kind;
  categories: Category[];
  canEdit: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [data, setData] = useState<ProductHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Filing an income source under "Groceries" is never what was meant, so
  // each side of the catalogue only offers its own categories.
  const choices = useMemo(
    () =>
      categories
        .filter((c) => !c.archived && (kind === 'income') === (c.bucket === 'INCOME'))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories, kind],
  );

  useEffect(() => {
    let cancelled = false;
    api
      .product(identity, id)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, [identity, id]);

  /** No `| null` on categoryId: an item always files somewhere (AGENTS.md §3.5b(ii)). */
  async function apply(patch: { categoryId: string }) {
    setBusy(true);
    try {
      const updated = await api.updateProduct(identity, id, patch);
      setData(updated);
      setError(null);
      onChanged();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!data) return;
    setBusy(true);
    try {
      await api.deleteProduct(identity, id);
      onDeleted();
    } catch (e: unknown) {
      setError(messageOf(e));
      setBusy(false);
    }
  }

  if (error) return <p className="error item-detail">{error}</p>;
  if (!data) return <p className="hint item-detail">Loading…</p>;

  const { product, purchases, currency, blockedBy } = data;

  // Cheapest and dearest unit price seen, which is the number that tells you
  // whether a habit is getting more expensive or you just buy it more often.
  const prices: Money[] = [];
  for (const purchase of purchases) {
    if (purchase.unitPrice === null) continue;
    try {
      prices.push(parseMoney(purchase.unitPrice, currency));
    } catch {
      /* a price the client cannot parse is one it should not draw a range from */
    }
  }
  const low = prices.reduce<Money | null>(
    (best, price) => (best === null || price.amount < best.amount ? price : best),
    null,
  );
  const high = prices.reduce<Money | null>(
    (best, price) => (best === null || price.amount > best.amount ? price : best),
    null,
  );

  const blockers: string[] = [];
  if (blockedBy.purchases > 0)
    blockers.push(
      `bought ${blockedBy.purchases} ${blockedBy.purchases === 1 ? 'time' : 'times'}`,
    );
  if (blockedBy.lists > 0)
    blockers.push(`on ${blockedBy.lists} shopping ${blockedBy.lists === 1 ? 'list' : 'lists'}`);

  // The way out differs. An item with purchases should be renamed — the
  // rename carries every one of them. An item only sitting on a list is one
  // line away from deletable, and saying "rename instead" there would be
  // advice for a problem the person does not have.
  const wayOut = blockedBy.purchases > 0 ? 'rename instead' : 'take it off those first';

  return (
    <div className="item-detail">
      <div className="item-detail-head">
        {/* No name here any more. The name is on the ROW above, where clicking
            it renames — the same gesture as a category (AGENTS.md §3.5b(vi)).
            Repeating it here was the duplication that hid the rename one level
            down and made the row's click do the wrong thing. */}
        <div className="item-stats">
          {product.lastUnitPrice && (
            <span>
              last <strong>{product.lastUnitPrice} {currency}</strong>
            </span>
          )}
          {low && high && low.amount !== high.amount && (
            <span>
              range{' '}
              <strong>
                {formatMoney(low)}–{formatMoney(high)}
              </strong>
            </span>
          )}
          {product.bucket && (
            <span>
              counts as <strong>{BUCKET_LABELS[product.bucket as Bucket]}</strong>
            </span>
          )}
        </div>

        {/* Delete only when nothing points at the item. A journal line reads
            its name THROUGH this row (`entry_item` has no name of its own), so
            deleting something you have bought would quietly blank those lines
            rather than fail. When it is blocked, say so instead of offering a
            button that cannot work. */}
        {canEdit &&
          (blockers.length === 0 ? (
            <button type="button" className="chip chip-ghost" onClick={() => void remove()} disabled={busy}>
              Delete
            </button>
          ) : (
            <span className="hint item-kept">
              {blockers.join(', ')} — {wayOut}
            </span>
          ))}
      </div>

      {/* The screen's real job. Everywhere else the app decides where a thing
          goes; here you do. */}
      {/* Income sources have no category to choose: they file under the one
          Income category, which is plumbing rather than a decision. */}
      {canEdit && kind !== 'income' && (
        <label className="item-file">
          <span className="field-label">Files under</span>
          {/* There is no "Unfiled" option. Every item files somewhere — an item
              outside every bucket is spending the app cannot see, and offering
              it as a choice made a gap look like a setting. An item CAN still
              arrive unfiled, orphaned when its category was deleted; then the
              placeholder below shows, cannot be chosen, and asks to be
              replaced. */}
          <select
            value={product.categoryId ?? ''}
            disabled={busy}
            onChange={(e) => {
              if (e.target.value) void apply({ categoryId: e.target.value });
            }}
          >
            {!product.categoryId && (
              <option value="" disabled>
                Not filed yet — choose one
              </option>
            )}
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="hint">
            Applies from the next purchase. The {purchases.length} already recorded keep the
            category they were entered with.
          </span>
        </label>
      )}

      {purchases.length === 0 ? (
        <p className="hint">No purchases recorded.</p>
      ) : (
        <ul className="purchases">
          {purchases.map((p, index) => (
            <li key={`${p.entryId}-${index}`}>
              <span className="purchase-when">{dayLabel(p.spentAt)}</span>
              <span className="purchase-where">
                {p.merchantName ?? (kind === 'income' ? '' : p.categoryName)}
                {p.quantity !== '1' && (
                  <em>
                    {' '}
                    {p.quantity}
                    {unitSuffix(p.unit)}
                  </em>
                )}
              </span>
              <span className="purchase-total">
                {p.lineTotal} {currency}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
